import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { FileText, ArrowUp, ArrowDown, FlaskConical, Leaf, Users, AlertTriangle, CheckCircle2, ArrowRight, Layers } from "lucide-react";

const Section = ({ children, className = "" }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}
  >
    {children}
  </motion.section>
);

const Kicker = ({ children }) => (
  <span className="text-xs uppercase tracking-widest font-semibold text-[#C87D55]">{children}</span>
);

export default function ResearchReport() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/content/research").then(({ data }) => setD(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  if (!d) {
    return (
      <div className="min-h-screen bg-[#F9F8F3]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-20"><div className="h-64 rounded-3xl bg-[#F3EFE6] animate-pulse" /></div>
      </div>
    );
  }

  const m = d.meta;

  return (
    <div className="min-h-screen bg-[#F9F8F3]" data-testid="research-page">
      <Navbar />

      {/* Header */}
      <section className="bg-[#1E3F20] text-white fb-grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 text-[#D99B26] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 mb-6">
            <FileText className="w-3.5 h-3.5" /> Science Deep Dive
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">{m.title}</h1>
          <p className="mt-5 text-lg text-[#C7D5C4] leading-relaxed max-w-2xl">{m.subtitle}</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { k: "Eingeschlossen", v: `${m.studies_included} Studien` },
              { k: "Screening", v: `${m.records_initial} Datensätze` },
              { k: "Methodik", v: m.method },
              { k: "Stand", v: m.date },
            ].map((s) => (
              <div key={s.k} className="bg-white/5 rounded-2xl ring-1 ring-white/10 p-4">
                <p className="text-xs uppercase tracking-widest text-[#8FA58C]">{s.k}</p>
                <p className="font-semibold text-white mt-1 text-sm">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary */}
      <Section className="py-16">
        <Kicker>Zusammenfassung</Kicker>
        <p className="mt-4 text-xl leading-relaxed text-[#1C2D24] max-w-3xl font-display">{d.summary}</p>
        <div className="mt-10 grid sm:grid-cols-4 gap-3">
          {d.funnel.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-5 text-center">
              <p className="font-display text-3xl font-bold text-[#2D5A27]">{f.n}</p>
              <p className="font-semibold text-[#1C2D24] text-sm mt-1">{f.label}</p>
              <p className="text-xs text-[#4A5D52] mt-1 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* NotebookLM Schaubilder */}
      <div className="bg-white py-16">
        <Section>
          <Kicker>Schaubilder</Kicker>
          <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24]">Die Evidenz auf einen Blick</h2>
          <p className="mt-3 text-[#4A5D52] max-w-2xl">Ausgewählte Grafiken fassen die zentralen Erkenntnisse dieses Deep Dive visuell zusammen.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-6" data-testid="deepdive-graphics">
            {[
              { src: "/deepdive/g1-methodik.png", cap: "Methodik-Trichter: aus 835 gefundenen Datensätzen blieben nach strenger Filterung 43 hochwertige Studien." },
              { src: "/deepdive/g2-achse.png", cap: "Paradigmenwechsel: vom klassischen Modell (Östrogenabfall → Knochenabbau) hin zur Darm-Knochen-Achse als aktivem Regelkreis." },
              { src: "/deepdive/g3-mechanismus.png", cap: "Drei Wirkwege der Probiotika – Metabolismus (Butyrat), Immunologie und Endokrinologie – vom Darm bis zur Knochenlamelle." },
              { src: "/deepdive/g4-waage.png", cap: "Die Knochenumbau-Waage: Knochenformation (P1NP +8,4 μg/L) gewinnt gegenüber der Resorption (CTX-I, SMD −0,35)." },
              { src: "/deepdive/g5-bmd.png", cap: "Wo Probiotika wirken: signifikanter Schutz der lumbalen Wirbelsäule, heterogenere Langzeitdaten an Tibia & Hüfte." },
              { src: "/deepdive/g6-matrix.png", cap: "Stamm-Referenzmatrix: welche Kulturen in welcher Dauer welchen primären Knochen-Effekt zeigen." },
            ].map((g) => (
              <figure key={g.src} className="rounded-2xl ring-1 ring-[#E2DDD3] overflow-hidden bg-white hover:shadow-lg transition-shadow">
                <img src={g.src} alt={g.cap} loading="lazy" className="w-full object-contain bg-[#F3EFE6]" />
                <figcaption className="p-4 text-sm text-[#4A5D52] leading-relaxed">{g.cap}</figcaption>
              </figure>
            ))}
          </div>
        </Section>
      </div>

      {/* Mechanisms */}
      <div className="bg-[#F3EFE6] py-16">
        <Section>
          <Kicker>Darm-Knochen-Achse</Kicker>
          <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24] flex items-center gap-3">
            <FlaskConical className="w-7 h-7 text-[#2D5A27]" /> Biologischer Mechanismus
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.mechanisms.map((mech) => (
              <div key={mech.title} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
                <h3 className="font-display text-lg font-bold text-[#1E3F20]">{mech.title}</h3>
                <p className="text-sm text-[#4A5D52] mt-2 leading-relaxed">{mech.text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Markers */}
      <Section className="py-16">
        <Kicker>Quantitative Evidenz</Kicker>
        <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24]">Die Knochenumbau-Waage</h2>
        <p className="mt-3 text-[#4A5D52] max-w-2xl">Multispezies-Formulierungen verschieben das Gleichgewicht messbar Richtung Knochenerhalt.</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {d.markers.map((mk) => (
            <div key={mk.marker} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-xl font-bold text-[#1C2D24]">{mk.marker}</span>
                {mk.dir === "up" ? <ArrowUp className="w-5 h-5 text-[#2D5A27]" /> : <ArrowDown className="w-5 h-5 text-[#C87D55]" />}
              </div>
              <p className="text-xs uppercase tracking-widest text-[#769777] mt-1">{mk.type}</p>
              <p className="font-display text-2xl font-bold text-[#2D5A27] mt-3">{mk.value}</p>
              <p className="text-xs text-[#4A5D52] mt-1">{mk.ci}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Dosing table */}
      <div className="bg-[#1E3F20] text-white py-16 fb-grain">
        <Section>
          <Kicker>Stamm-Referenzmatrix</Kicker>
          <h2 className="mt-3 font-display text-3xl font-bold text-white">Das Probiotika-Formularium</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl ring-1 ring-white/10">
            <table className="w-full text-left text-sm min-w-[640px]">
              <thead className="bg-white/10 text-[#C7D5C4]">
                <tr>
                  <th className="px-4 py-3 font-semibold">Stamm / Formulierung</th>
                  <th className="px-4 py-3 font-semibold">Dosis</th>
                  <th className="px-4 py-3 font-semibold">Dauer</th>
                  <th className="px-4 py-3 font-semibold">Design</th>
                  <th className="px-4 py-3 font-semibold">Klinischer Effekt</th>
                </tr>
              </thead>
              <tbody>
                {d.dosing_table.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-white/5" : ""}>
                    <td className="px-4 py-3 font-medium text-white">{r.strain}</td>
                    <td className="px-4 py-3 text-[#D99B26] whitespace-nowrap">{r.dose}</td>
                    <td className="px-4 py-3 text-[#C7D5C4] whitespace-nowrap">{r.duration}</td>
                    <td className="px-4 py-3 text-[#C7D5C4] whitespace-nowrap">{r.design}</td>
                    <td className="px-4 py-3 text-[#C7D5C4]">{r.effect}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* Natural foods */}
      <Section className="py-16">
        <Kicker>Frage 3</Kicker>
        <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24] flex items-center gap-3">
          <Leaf className="w-7 h-7 text-[#2D5A27]" /> Natürliche Lebensmittel
        </h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {d.natural_foods.map((f) => (
            <div key={f.food} className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
              <h3 className="font-display text-lg font-bold text-[#1E3F20]">{f.food}</h3>
              <p className="text-sm text-[#4A5D52] mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 bg-[#E2EBE1] rounded-2xl p-6 text-[#1E3F20] leading-relaxed">{d.foods_verdict}</div>
      </Section>

      {/* Recommendations */}
      <div className="bg-[#F3EFE6] py-16">
        <Section>
          <Kicker>Praxis</Kicker>
          <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24] flex items-center gap-3">
            <Users className="w-7 h-7 text-[#2D5A27]" /> Empfehlungen
          </h2>
          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
              <h3 className="font-semibold text-[#1C2D24] mb-4">Für Therapeut:innen</h3>
              <ul className="space-y-3">
                {d.clinician_recos.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#4A5D52]"><CheckCircle2 className="w-5 h-5 text-[#2D5A27] shrink-0 mt-0.5" />{r}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-6">
              <h3 className="font-semibold text-[#1C2D24] mb-4">Für Patient:innen</h3>
              <ul className="space-y-3">
                {d.patient_recos.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#4A5D52]"><CheckCircle2 className="w-5 h-5 text-[#C87D55] shrink-0 mt-0.5" />{r}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      </div>

      {/* Triangle + conclusions */}
      <Section className="py-16">
        <Kicker>Synthese</Kicker>
        <h2 className="mt-3 font-display text-3xl font-bold text-[#1C2D24] flex items-center gap-3">
          <Layers className="w-7 h-7 text-[#2D5A27]" /> Das adjuvante Dreieck
        </h2>
        <div className="mt-8 space-y-3">
          {d.triangle.map((t, i) => (
            <div key={i} className="flex items-start gap-4 bg-white rounded-2xl ring-1 ring-[#E2DDD3] p-5">
              <span className="shrink-0 font-display font-bold text-[#769777] text-lg">{i + 1}</span>
              <div>
                <h3 className="font-semibold text-[#1E3F20]">{t.level}</h3>
                <p className="text-sm text-[#4A5D52] mt-1">{t.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {d.conclusions.map((c, i) => (
            <div key={i} className="bg-[#1E3F20] text-white rounded-2xl p-6">
              <h3 className="font-semibold text-[#D99B26] text-sm">{c.q}</h3>
              <p className="text-sm text-[#C7D5C4] mt-2 leading-relaxed">{c.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Limitations */}
      <Section className="pb-16">
        <div className="bg-[#FCF3E7] border border-[#E8D3B5] rounded-2xl p-6">
          <h3 className="flex items-center gap-2 font-semibold text-[#8a5a2b] mb-3"><AlertTriangle className="w-5 h-5" /> Limitationen &amp; Forschungsbedarf</h3>
          <ul className="grid sm:grid-cols-2 gap-2">
            {d.limitations.map((l, i) => (
              <li key={i} className="text-sm text-[#8a5a2b] flex items-start gap-2"><span className="mt-1">•</span>{l}</li>
            ))}
          </ul>
          <p className="text-xs text-[#a67c4e] mt-4">Basierend auf {d.references_count} referenzierten Publikationen. Kein Ersatz für ärztliche Beratung.</p>
        </div>

        <div className="mt-10 bg-[#2D5A27] rounded-3xl p-8 sm:p-10 text-center">
          <h3 className="font-display text-2xl font-bold text-white">Konkrete Umsetzung im Premium-Bereich</h3>
          <p className="text-[#C7D5C4] mt-2 max-w-xl mx-auto">Rezepte, Ernährungspläne, eine Schritt-für-Schritt-Probiotika-Kur und eine Produkt-Marktanalyse – dauerhaft für 19,99 €.</p>
          <Link to="/premium" data-testid="research-premium-cta" className="mt-6 inline-flex items-center gap-2 bg-[#D99B26] hover:bg-[#c68b1e] text-[#1E3F20] font-semibold rounded-full px-7 py-3 transition-colors">
            Premium freischalten <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      <Footer />
    </div>
  );
}
